import React, { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Link } from 'react-router-dom';
import { FaSearch, FaRobot, FaCode, FaDownload, FaInstagram } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';

function Automation() {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const { user } = useAuth();

  useEffect(() => {
    const fetchAutomations = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, 'automations'));
        const automationList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setAutomations(automationList);
      } finally {
        setLoading(false);
      }
    };

    fetchAutomations();
  }, []);

  const filteredAutomations = automations.filter(automation => {
    const matchesSearch = automation.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         automation.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !categoryFilter || automation.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const categories = [...new Set(automations.map(a => a.category))];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-20">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center">
              Excel Otomasyonları
            </h1>
            <p className="text-xl text-center text-indigo-100 mb-8">
              İş süreçlerinizi hızlandıracak hazır Excel ve VBA çözümleri
            </p>
            <div className="flex justify-center">
              <a
                href="https://www.instagram.com/excelvbamakro/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:opacity-90 transition-opacity"
              >
                <FaInstagram className="mr-2 text-xl" />
                Bizi Instagram'da Takip Edin
              </a>
            </div>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-gray-50"></div>
      </div>

      {/* Search and Filter Section */}
      <div className="container mx-auto px-4 -mt-8 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Otomasyon ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="">Tüm Kategoriler</option>
                {categories.map((category, index) => (
                  <option key={`category-${category}-${index}`} value={category}>{category}</option>
                ))}
              </select>
            </div>
            {user && (user.role === 'admin' || user.role === 'editor') && (
              <Link
                to="/admin/automations/new"
                className="inline-flex items-center justify-center px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                <FaRobot className="mr-2" />
                Yeni Otomasyon
              </Link>
            )}
          </div>
        </motion.div>

        {/* Automation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredAutomations.map((automation, index) => (
            <motion.div
              key={automation.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow"
            >
              {automation.files?.find(f => f.type.startsWith('image/')) ? (
                <img
                  src={automation.files.find(f => f.type.startsWith('image/')).data}
                  alt={automation.title}
                  className="w-full h-48 object-cover"
                />
              ) : (
                <div className="w-full h-48 bg-gradient-to-br from-indigo-100 to-purple-50 flex items-center justify-center">
                  <FaRobot className="text-5xl text-indigo-300" />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center mb-2">
                  <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-full text-sm">
                    {automation.category}
                  </span>
                </div>
                <h2 className="text-2xl font-bold mb-3 text-gray-800 line-clamp-2">
                  {automation.title}
                </h2>
                <p className="text-gray-600 mb-4 line-clamp-3">
                  {automation.description}
                </p>
                <div className="flex items-center justify-between mt-4">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center text-sm text-gray-500">
                      <FaCode className="mr-1" />
                      VBA
                    </span>
                    <span className="flex items-center text-sm text-gray-500">
                      <FaDownload className="mr-1" />
                      {automation.downloads || 0}
                    </span>
                  </div>
                  <Link
                    to={`/automation/${automation.id}`}
                    className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    Detayları Gör
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </Link>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {filteredAutomations.length === 0 && (
          <div className="text-center py-16">
            <FaRobot className="mx-auto text-6xl text-gray-300 mb-4" />
            <p className="text-xl text-gray-500">
              {searchTerm || categoryFilter
                ? 'Aramanızla eşleşen otomasyon bulunamadı.'
                : 'Henüz otomasyon bulunmuyor.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Automation; 